import { useEffect } from 'react';

import { GlobalScrollLock } from 'components/GlobalScrollLock';

import * as styled from '../styles/intent-modal';

type Props = {
  providerName: string;
  accentColor: string;
  available: Profile.IntentKey[];
  onPick: (intent: Profile.IntentKey) => void;
  onClose: () => void;
};

const OPTIONS: Record<
  Exclude<Profile.IntentKey, 'message'>,
  { emoji: string; title: (name: string) => string; subtitle: string }
> = {
  book: {
    emoji: '📅',
    title: (name) => `A specific time with ${name}`,
    subtitle: 'Pick a slot. Fixed time, fixed price.',
  },
  project: {
    emoji: '🛠',
    title: () => 'Help with a custom job',
    subtitle: "Describe what you need — they'll scope it and get back to you.",
  },
  collab: {
    emoji: '💼',
    title: () => 'A paid collaboration',
    subtitle:
      'For brands and businesses with a campaign or sponsorship in mind.',
  },
};

export const IntentModal = ({
  providerName,
  accentColor,
  available,
  onPick,
  onClose,
}: Props) => {
  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const choices = available.filter(
    (intentKey): intentKey is Exclude<Profile.IntentKey, 'message'> =>
      intentKey !== 'message',
  );
  const showMessageFallback = available.includes('message');

  return (
    <styled.Overlay onClick={onClose}>
      <GlobalScrollLock />
      <styled.Sheet onClick={(event) => event.stopPropagation()}>
        <styled.Header>
          <styled.Title>What do you need from {providerName}?</styled.Title>
          <styled.CloseBtn type="button" onClick={onClose} aria-label="Close">
            ✕
          </styled.CloseBtn>
        </styled.Header>

        <styled.OptionsList>
          {choices.map((key) => {
            const option = OPTIONS[key];
            return (
              <styled.OptionRow
                key={key}
                $accent={accentColor}
                onClick={() => onPick(key)}
              >
                <styled.OptionEmoji>{option.emoji}</styled.OptionEmoji>
                <styled.OptionBody>
                  <styled.OptionTitle>
                    {option.title(providerName)}
                  </styled.OptionTitle>
                  <styled.OptionSubtitle>
                    {option.subtitle}
                  </styled.OptionSubtitle>
                </styled.OptionBody>
                <styled.OptionChevron $accent={accentColor}>
                  →
                </styled.OptionChevron>
              </styled.OptionRow>
            );
          })}
        </styled.OptionsList>

        {showMessageFallback ? (
          <styled.FallbackRow>
            Not sure?{' '}
            <styled.FallbackLink
              $accent={accentColor}
              onClick={() => onPick('message')}
            >
              Send a message →
            </styled.FallbackLink>
          </styled.FallbackRow>
        ) : null}
      </styled.Sheet>
    </styled.Overlay>
  );
};
